import {
 BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { MenuObjectType, UpsellResponse } from 'app/features/Order/types/OrderTypes';
import { YextLocationFilterType, YextLocationResponseType } from "app/features/Location/types/YextLocationResponseType";
import { encryptURI } from 'app/shared/utils/ValueEncryption';
import { StoreResponseType } from "app/features/Location/store/StoreResponseType";
import { GetAllRewardsResponse } from "app/features/Rewards/types/RewardsDetails";
import { GetUserRewardsDetails } from "app/features/Rewards/types/UserRewardsDetails";
import { FinalOrderResponse } from "app/features/Order/types/FinalOrderResponseType";
import { GetBalanceRequest, RewardsAccountResponse, RewardsEntity } from "app/features/Categories/types/RewardsAccountType";
import { ResetPasswordResponseType } from "app/features/MyAccount/ResetPassword/types/ResetPasswordType";
import { GiftCardResponse } from 'app/features/GiftCards/GiftCardResponse';
import { GetPendingOrdersResponse, GetRecentOrdersResponse } from 'app/features/RecentOrders/types/Order';

// eslint-disable-next-line import/no-cycle
import {
 setLogoutRequested,
} from 'app/store/slices/authSlice';
import { UNAUTHORIZED_CODE } from 'app/shared/constants/constants';
import { encrypt, decrypt } from 'app/shared/utils/EncryptionUtility';
import getBrandName from 'app/shared/utils/BrandHelper';
import { EncryptedResponse } from "./commonTypes";
import ApiConstants from "./apiConstants";
import getHeader from './getHeader';

const baseUrl = process.env.REACT_APP_ALLEGIANCE_API_URL;
const { appSource } = getBrandName();
const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => getHeader(getState, headers),
  credentials: window.location.hostname === 'localhost' ? 'include' : undefined,
});

export const customBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === UNAUTHORIZED_CODE) {
    // Run logout
    api.dispatch(setLogoutRequested(true));
  }

  return result;
};
// Define a service using a base URL and expected endpoints
export const fetchGateAPI = createApi({
    reducerPath: 'menu',
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
    getStoreEligibility: builder.query<boolean, string>({
        query: (storeId) => ({
            url: `api/storeEligibility/${storeId}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getAllProducts: builder.query<MenuObjectType, string>({
        query: (storeNumber) => ({
            url: `api/store/${encryptURI(storeNumber)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getAllLocations: builder.query<YextLocationResponseType, YextLocationFilterType>({
        query: (ylc) => ({
            url: `api/location/${encryptURI(ylc.lat.toString())}/${encryptURI(ylc.long.toString())}/${encryptURI(ylc.miles.toString())}/${encryptURI(ylc.yextBrandValue)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getStoreInfo: builder.query<StoreResponseType, string>({
        query: (storeNumber) => ({
            url: `api/location/${encryptURI(storeNumber)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getTaxAndValidationInfo: builder.query<FinalOrderResponse, string | undefined>({
        query: (orderNumber) => ({
            url: `api/tax/${encryptURI(orderNumber || '')}`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getRecentOrdersInfo: builder.query<GetRecentOrdersResponse, string | undefined>({
        query: () => ({
            url: `/api/recent/orders`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getPendingOrdersInfo: builder.query<GetPendingOrdersResponse, void>({
        query: () => ({
            url: ApiConstants.PENDING_ORDERS,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getAllRewards: builder.query<GetAllRewardsResponse, string | undefined>({
        query: () => ({
            url: `/api/allRewards`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getUserRewards: builder.query<GetUserRewardsDetails, string | null>({
        query: (user) => ({
            url: `/api/user/rewards/${encryptURI(user || '')}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getClaimedRewards: builder.query<RewardsEntity[], string | null>({
        query: (user) => ({
            url: `/api/claim/rewards/${encryptURI(user || '')}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getOrderDetailsFromRecentOrder: builder.query<FinalOrderResponse, string | undefined>({
        query: (orderNumber) => ({
            url: `/api/recent/orders/${encryptURI(orderNumber || '')}/orderAgain`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getNutritionURL: builder.query<string, string>({
        query: (brandName) => ({
            url: `/api/nutritionURL/${encryptURI(brandName)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    validatePasswordResetToken: builder.query<ResetPasswordResponseType, string | undefined>({
        query: (token) => ({
            url: `${ApiConstants.VALIDATE_RESET_PASSWORD_TOKEN}${encryptURI(token || '')}`,
            method: 'GET',
        }),
    }),
    getConfigs: builder.query<EncryptedResponse, string>({
        query: (brandName) => ({
            url: `${ApiConstants.GET_CONFIGS}${encryptURI(brandName)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getCustomerCards: builder.query<EncryptedResponse, string | undefined>({
        query: (fdCustomerId = '') => ({
            url: ApiConstants.CUSTOMER_CARDS.replace(':fdCustomerId', encryptURI(fdCustomerId)),
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getGiftCardContent: builder.query<GiftCardResponse[], string>({
        query: (brandName: string) => ({
            url: `${ApiConstants.GIFT_CARD_CONTENT}${encryptURI(brandName)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getSuggestedProducts: builder.query<UpsellResponse, string | undefined>({
        query: () => ({
            url: ApiConstants.UPSELL_PRODUCTS,
            method: 'GET',
        }),
        keepUnusedDataFor: Number(process.env.REACT_APP_CACHE_REFRESH),
    }),
    getApplicableRewards: builder.query<GetAllRewardsResponse, string>({
        query: (orderNumber) => ({
            url: `/api/applicable_rewards/${encryptURI(orderNumber)}`,
            method: 'GET',
        }),
        keepUnusedDataFor: 0,
    }),
    getBalance: builder.query<RewardsAccountResponse, GetBalanceRequest>({
        query: (balanceRequest) => ({
            url: `api/getBalance`,
            method: 'POST',
            body: JSON.stringify({
                value: encrypt(`{"uid":"${balanceRequest.user}",
                "sourceApp":"${appSource}",
                "getBalance":{"checkRegistration":false},
                "cardNumber":"${balanceRequest.cardNumber}"}`),
            }),
        }),
        transformResponse: (response: EncryptedResponse) => {
            const decrypted = JSON.parse(decrypt(response.value)) as RewardsAccountResponse;
            return decrypted;
        },
    }),
    }),
});

export const {
    useGetStoreEligibilityQuery,
    useGetAllProductsQuery,
    useGetAllLocationsQuery,
    useGetTaxAndValidationInfoQuery,
    useGetStoreInfoQuery,
    useGetRecentOrdersInfoQuery,
    useGetPendingOrdersInfoQuery,
    useGetAllRewardsQuery,
    useGetUserRewardsQuery,
    useGetClaimedRewardsQuery,
    useGetOrderDetailsFromRecentOrderQuery,
    useValidatePasswordResetTokenQuery,
    useGetConfigsQuery,
    useGetCustomerCardsQuery,
    useGetGiftCardContentQuery,
    useGetSuggestedProductsQuery,
    useGetApplicableRewardsQuery,
    useGetBalanceQuery,
} = fetchGateAPI;
